import {
  PAYMENT_CATEGORY,
  OVERRIDE_BLOCKED_STATUSES,
  OVERRIDE_TOOLTIPS,
} from '../constants';

/**
 * Determine whether a payment is the last UNLOCKED INVOICE component from a template.
 * The last unlocked component absorbs remainder in percentage absorption, so its
 * value must stay system-managed to guarantee the total matches the invoice.
 *
 * Mirrors the backend logic: the "last" component is the highest-indexed one
 * that is NOT already locked (PAID or AWAITING_PAYMENT).
 */
export const isLastInvoiceComponent = (payment, allPayments) => {
  if (!payment.payment_term_component || payment.category !== PAYMENT_CATEGORY.INVOICE) {
    return false;
  }

  const invoicePayments = allPayments.filter(
    (p) => p.payment_term_component && p.category === PAYMENT_CATEGORY.INVOICE
  );

  // Single component = no remainder absorption, override is safe
  if (invoicePayments.length <= 1) return false;

  // Locked statuses — these components can no longer change
  const LOCKED_STATUSES = ['PAID', 'AWAITING_PAYMENT'];
  const lockedIndices = new Set(
    invoicePayments
      .filter((p) => p.id !== payment.id && LOCKED_STATUSES.includes(p.status))
      .map((p) => p.payment_term_component_order_index)
  );

  // Find the last UNLOCKED component order_index
  let lastUnlockedIndex = null;
  [...invoicePayments]
    .sort((a, b) => (a.payment_term_component_order_index || 0) - (b.payment_term_component_order_index || 0))
    .forEach((p) => {
      if (!lockedIndices.has(p.payment_term_component_order_index)) {
        lastUnlockedIndex = p.payment_term_component_order_index;
      }
    });

  return payment.payment_term_component_order_index === lastUnlockedIndex;
};

/**
 * Determine the disabled-override tooltip for a payment, or null if override is allowed.
 */
export const getOverrideDisabledReason = (payment, allPayments) => {
  if (OVERRIDE_BLOCKED_STATUSES.includes(payment.status)) {
    return OVERRIDE_TOOLTIPS[payment.status];
  }
  if (isLastInvoiceComponent(payment, allPayments)) {
    return OVERRIDE_TOOLTIPS.LAST_INVOICE;
  }
  return null;
};

/**
 * Whether the USD/BRL value inputs of the row currently being edited should be
 * disabled. System-managed (or auto-generated) payments stay locked until the
 * user explicitly overrides their values; manual payments are always editable.
 *
 * NOTE: This MUST NOT depend on the visibility of the "Add Payment" form —
 * doing so wrongly couples the row read-only state to the create-form toggle
 * (see ticket 86agkk6hh).
 */
export const isValueFieldDisabled = (payment, isValueOverridden) => {
  if (!payment) return false;
  return (
    (payment.is_system_managed || payment.is_auto_generated) && !isValueOverridden
  );
};

/**
 * Whether the Category (or other template-controlled) field on the row currently
 * being edited should be disabled. Locked on payments whose category is derived
 * from an upstream source — either the PaymentTerm template (`is_system_managed`)
 * or the automatic backfill / sync pipeline (`is_auto_generated`). Manual payments:
 * editable.
 *
 * MUST NOT depend on `showCreateForm` — see ticket 86agy6t2x (follow-up to 86agkk6hh).
 */
export const isTemplateControlledFieldDisabled = (payment) => {
  if (!payment) return false;
  return Boolean(payment.is_system_managed || payment.is_auto_generated);
};

/**
 * Whether the Due Date field on the row currently being edited should be disabled.
 * Locked whenever the template component can auto-calculate the date from an
 * offset (`payment_term_component_has_date_offset`), regardless of whether the
 * value is already populated — the system will eventually compute it, so any
 * manual entry would be overwritten. Manual payments and template components
 * without a date offset: editable.
 *
 * MUST NOT depend on `showCreateForm` — see ticket 86agy6t2x.
 */
export const isDueDateFieldDisabled = (payment) => {
  if (!payment) return false;
  return Boolean(payment.payment_term_component_has_date_offset);
};

/**
 * Sx overrides applied to enabled value fields after override to add blue highlight.
 * Uses :not(.Mui-disabled) so disabled fields (e.g. percentage) stay neutral.
 */
export const OVERRIDDEN_FIELD_SX = {
  '& .MuiOutlinedInput-root:not(.Mui-disabled) fieldset': {
    borderColor: '#1976d2',
    borderWidth: 2,
  },
  '& .MuiOutlinedInput-root:not(.Mui-disabled)': {
    boxShadow: '0 0 0 3px rgba(25,118,210,0.08)',
  },
};
